import * as React from "react"
import { createContext, useMemo } from "react"

import { authClient, useSession } from "#lib/auth-client.ts"

type User = {
  id: string
  email: string
  name: string
  image: string | null
  role: string
  phone: string | null
  organizationId: string
  emailVerified: boolean
}

type AuthContext = {
  user: User | null
  isPending: boolean
  isAuthenticated: boolean
  sendMagicLink: (email: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContext | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession()
  const [initializing, setInitializing] = React.useState(true)

  React.useEffect(() => {
    if (!isPending) setInitializing(false)
  }, [isPending])

  const user = (session?.user as User | undefined) ?? null
  const isLoading = isPending || initializing

  const value = useMemo<AuthContext>(
    () => ({
      user,
      isPending: isLoading,
      isAuthenticated: !!user,
      sendMagicLink: async (email: string) => {
        await authClient.signIn.magicLink({ email, callbackURL: "/login-verify" })
      },
      logout: async () => {
        await authClient.signOut()
      },
    }),
    [user, isLoading],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
